// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React from 'react';
import { Header } from '@amzn/awsui-components-react-v3/polaris';
import ListTable, { ListColumn } from '../../Common/ListTable';
import { ARC_FILTERING_PLACEHOLDER, describeArc } from '../../../utils/arcRows';
import { formatLocale } from '../../../utils/locales';
import {
  BasePriceModifier,
  MATCH_BANDS,
  MatchBand,
  REPETITION,
  storedWeightText,
} from './scopingModels';

const bandWeight = (modifier: BasePriceModifier, name: string): string => {
  const band = modifier.matchPercentPriceModifiers.find(candidate => candidate.name === name);
  return band ? storedWeightText(band.percentage) : '-';
};

// Pricing names each generated band by the match percentage it starts at.
const MATCH_BAND_NAMES: Record<MatchBand, string> = {
  zeroTo49: 'p0',
  fiftyTo74: 'p50',
  p75: 'p75',
  p85: 'p85',
  p95: 'p95',
  p100: 'p100',
  p101: 'p101',
};

const COLUMNS: ListColumn<BasePriceModifier>[] = [
  {
    id: 'sourceLocale',
    header: 'Source locale',
    cell: (modifier): string =>
      formatLocale(modifier.languagePair && modifier.languagePair.sourceLocale),
  },
  {
    id: 'targetLocale',
    header: 'Target locale',
    cell: (modifier): string =>
      formatLocale(modifier.languagePair && modifier.languagePair.targetLocale),
  },
  ...MATCH_BANDS.map(({ band, weight, label }) => ({
    id: weight,
    header: label,
    cell: (modifier: BasePriceModifier): string => bandWeight(modifier, MATCH_BAND_NAMES[band]),
  })),
  {
    id: REPETITION.field,
    header: REPETITION.label,
    cell: (modifier: BasePriceModifier): string =>
      storedWeightText(modifier.repetitionPriceModifier.percentage),
  },
];

/** The weights Pricing generated for the latest draft, per base-rate arc that is not excluded. */
const ScopingModelWeights = ({ modifiers }: { modifiers: BasePriceModifier[] }): JSX.Element => {
  return (
    <ListTable
      id="scopingModelWeights"
      items={modifiers}
      columns={COLUMNS}
      filterText={(modifier): string =>
        describeArc(modifier.languagePair || { sourceLocale: null, targetLocale: null })
      }
      filteringPlaceholder={ARC_FILTERING_PLACEHOLDER}
      header={
        <Header
          variant="h2"
          counter={`(${modifiers.length})`}
          description="Generated by Pricing for the latest change set, 1 being 100% of the rate."
        >
          Weights
        </Header>
      }
      empty="No weights."
    />
  );
};

export default ScopingModelWeights;
